// ReconcileButton — Finance Board → edit scope numbers + emit addendum PDF.
// May 13, 2026.
//
// Shannon-facing: on any Finance Board card she can tap "🧮 Reconcile" to open
// the existing ScopeEditorModal pre-loaded with the deal's full record. On
// save the helper:
//   1. Computes a field-level diff against the snapshot captured at modal open
//   2. Inserts an activity_log row tagged `finance_reconcile` with the diff
//   3. Generates an Install Agreement Addendum PDF reflecting the new totals
//   4. Uploads the addendum to deal-documents storage + writes a deal_documents row
// Step 3/4 only fire if at least one numeric scope field actually changed and
// the deal had a signed install agreement on file.

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import type { Deal } from "../../lib/types";
import { calcScope } from "../../lib/scopeCalc";
import { ScopeEditorModal } from "../deal/ScopeSection";

// Fields the ScopeEditorModal lets the user edit. Each row defines:
//   key      — DB column name
//   label    — UI / addendum label
//   isMoney  — true → diff renders as currency
//   isBool   — true → renders before/after as "Yes" / "No"
type FieldKind = "money" | "bool" | "text";
type FieldSpec = { key: keyof Deal; label: string; kind: FieldKind };

const RECONCILE_FIELDS: FieldSpec[] = [
  { key: "rcv_total",                  label: "RCV",                          kind: "money" },
  { key: "depreciation_total",         label: "Recoverable Depreciation",     kind: "money" },
  { key: "deductible",                 label: "Deductible",                   kind: "money" },
  { key: "supplements",                label: "Supplements (legacy total)",   kind: "money" },
  { key: "supplement_invoice_amount",  label: "Supplement Invoice Amount",    kind: "money" },
  { key: "supplement_approved_amount", label: "Approved Supplement Amount",   kind: "money" },
  { key: "pwi_code",                   label: "PWI",                          kind: "money" },
  { key: "acv_credit",                 label: "Un-performed ACV credit",      kind: "money" },
  { key: "dep_credit",                 label: "Un-performed DEP credit",      kind: "money" },
  { key: "upgrades",                   label: "Upgrades",                     kind: "money" },
  { key: "retail_profit",              label: "Retail profit add-ons",        kind: "money" },
  { key: "at_cost_retail",             label: "@-cost retail",                kind: "money" },
  { key: "misc_credit",                label: "Misc credit",                  kind: "money" },
  { key: "non_commissionable",         label: "Non-commissionable",           kind: "money" },
  { key: "acv_only",                   label: "ACV-only flag",                kind: "bool" },
  { key: "items_not_performing_lines", label: "Un-performed line items",      kind: "text" },
];

function getNumber(d: Partial<Deal>, k: keyof Deal): number {
  const v = (d as Record<string, unknown>)[k as string];
  return typeof v === "number" ? v : Number(v) || 0;
}

function getBool(d: Partial<Deal>, k: keyof Deal): boolean {
  return (d as Record<string, unknown>)[k as string] === true;
}

function getText(d: Partial<Deal>, k: keyof Deal): string {
  const v = (d as Record<string, unknown>)[k as string];
  return typeof v === "string" ? v.trim() : "";
}

export default function ReconcileButton({
  dealId,
  className,
  size = "sm",
  label = "🧮 Reconcile",
}: {
  dealId: string;
  className?: string;
  size?: "xs" | "sm";
  label?: string;
}) {
  const { role, isSuperUser, member } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deal, setDeal] = useState<Deal | null>(null);
  // Snapshot the deal at modal-open time so we can diff after save.
  const [snapshot, setSnapshot] = useState<Deal | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function fetchAndOpen() {
    setErr(null);
    setLoading(true);
    try {
      const { data, error } = await supabase.from("deals").select("*").eq("id", dealId).single();
      if (error) throw error;
      const full = data as Deal;
      setDeal(full);
      setSnapshot(full);
      setOpen(true);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function handleSaved(payload: Partial<Deal>) {
    // payload contains the fields ScopeEditorModal wrote — but for diff we want
    // to compare snapshot vs. the merged-after view (snapshot + payload).
    if (!snapshot) {
      setOpen(false);
      return;
    }
    const after = { ...snapshot, ...payload };

    // Build the diff
    type Change = { key: keyof Deal; label: string; kind: FieldKind; before: number | string | boolean | null; after: number | string | boolean | null };
    const changes: Change[] = [];
    for (const f of RECONCILE_FIELDS) {
      if (f.kind === "money") {
        const b = getNumber(snapshot, f.key);
        const a = getNumber(after, f.key);
        if (a !== b) changes.push({ key: f.key, label: f.label, kind: f.kind, before: b, after: a });
      } else if (f.kind === "bool") {
        const b = getBool(snapshot, f.key);
        const a = getBool(after, f.key);
        if (a !== b) changes.push({ key: f.key, label: f.label, kind: f.kind, before: b, after: a });
      } else {
        const b = getText(snapshot, f.key);
        const a = getText(after, f.key);
        if (a !== b) changes.push({ key: f.key, label: f.label, kind: f.kind, before: b, after: a });
      }
    }

    // No changes — just close the modal and skip logging / addendum.
    if (changes.length === 0) {
      setOpen(false);
      return;
    }

    // Step 1: write activity_log entry. Best-effort — don't block the modal close.
    try {
      const moneyOnly = changes.filter((c) => c.kind === "money");
      const totalDelta = moneyOnly.reduce((s, c) => s + ((Number(c.after) || 0) - (Number(c.before) || 0)), 0);
      const summary =
        moneyOnly.length > 0
          ? `${changes.length} field${changes.length === 1 ? "" : "s"} updated · net ${totalDelta >= 0 ? "+" : "−"}$${Math.abs(totalDelta).toLocaleString()}`
          : `${changes.length} field${changes.length === 1 ? "" : "s"} updated`;
      await supabase.from("activity_log").insert({
        deal_id: dealId,
        activity_type: "finance_reconcile",
        title: `Scope reconciled by ${member?.full_name ?? "Finance"}`,
        description: summary,
        metadata: {
          by: member?.full_name ?? null,
          by_user_id: member?.id ?? null,
          changes: changes.map((c) => ({
            field: String(c.key),
            label: c.label,
            kind: c.kind,
            before: c.before,
            after: c.after,
          })),
        },
      });
    } catch (e) {
      console.warn("[reconcile] activity_log insert failed", e);
    }

    // Step 2: generate addendum PDF if the original agreement was signed AND
    // any money field changed. Skipped for text-only changes (no numbers to
    // restate) and for unsigned deals (no original to amend).
    const moneyChanged = changes.some((c) => c.kind === "money");
    const wasSigned = snapshot.install_agreement_signed === true || !!snapshot.install_agreement_pdf_url;
    if (moneyChanged && wasSigned) {
      void generateAndUploadAddendum(after, snapshot, changes).catch((e) => {
        console.warn("[reconcile] addendum generation failed", e);
      });
    }

    setOpen(false);
  }

  async function generateAndUploadAddendum(
    afterDeal: Deal,
    beforeDeal: Deal,
    changes: Array<{ key: keyof Deal; label: string; kind: FieldKind; before: number | string | boolean | null; after: number | string | boolean | null }>,
  ) {
    const { generateAddendumPDF } = await import("../../lib/installAgreementAddendumPDF");

    // Pick a sequential addendum number based on existing addenda for this deal.
    let addendumNumber = "ADD-001";
    try {
      const { data: existing } = await supabase
        .from("deal_documents")
        .select("file_name")
        .eq("deal_id", dealId)
        .eq("file_type", "install_agreement_addendum");
      const count = (existing?.length ?? 0) + 1;
      addendumNumber = `ADD-${String(count).padStart(3, "0")}`;
    } catch {
      // fall back to ADD-001
    }

    const beforeScope = calcScope(beforeDeal);
    const blob = await generateAddendumPDF({
      deal: afterDeal,
      addendumNumber,
      changes: changes
        .filter((c) => c.kind === "money" || c.kind === "bool")
        .map((c) => ({
          label: c.label,
          before:
            c.kind === "bool"
              ? (c.before === true ? "Yes" : "No")
              : (typeof c.before === "number" ? c.before : Number(c.before) || 0),
          after:
            c.kind === "bool"
              ? (c.after === true ? "Yes" : "No")
              : (typeof c.after === "number" ? c.after : Number(c.after) || 0),
          isMoney: c.kind === "money",
        })),
      beforeScope,
      reconciledByName: member?.full_name ?? null,
    });

    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    const fileName = `${addendumNumber}-addendum.pdf`;
    const path = `install-agreement-addenda/${dealId}/${ts}-${fileName}`;

    const { error: upErr } = await supabase.storage
      .from("deal-documents")
      .upload(path, blob, { contentType: "application/pdf" });
    if (upErr) throw upErr;

    const { error: docErr } = await supabase.from("deal_documents").insert({
      deal_id: dealId,
      file_name: fileName,
      file_type: "install_agreement_addendum",
      storage_path: path,
      file_size: blob.size,
      mime_type: "application/pdf",
      uploaded_by: member?.id ?? null,
    });
    if (docErr) {
      // best-effort cleanup of orphan blob
      void supabase.storage.from("deal-documents").remove([path]);
      throw docErr;
    }
  }

  // Reset state when the modal closes from outside (e.g. backdrop click)
  useEffect(() => {
    if (!open) {
      setDeal(null);
      setSnapshot(null);
    }
  }, [open]);

  const sizeClass = size === "xs"
    ? "text-[10px] px-2 py-1"
    : "text-[12px] px-3 py-1.5";

  return (
    <>
      <button
        type="button"
        onClick={fetchAndOpen}
        disabled={loading}
        className={
          (className ?? "") +
          " " + sizeClass +
          " font-semibold rounded-lg border bg-white border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition inline-flex items-center gap-1"
        }
        title="Edit scope numbers and emit an install-agreement addendum"
      >
        {loading ? "Loading…" : label}
      </button>
      {err && (
        <span className="ml-2 text-[11px] text-rose-700">{err}</span>
      )}
      {open && deal && (
        <ScopeEditorModal
          deal={deal}
          open={open}
          role={role}
          isSuperUser={isSuperUser}
          member={member}
          onClose={() => setOpen(false)}
          onSaved={(payload) => { void handleSaved(payload); }}
        />
      )}
    </>
  );
}
